import { useState } from 'react'
import { useTableColumns } from '@/hooks/useTableColumns'
import type { TableColumnDef } from '@/lib/tableColumnPrefs'
import { ColumnCells, ResizableTh } from '@/components/table'
import { Link, useNavigate } from 'react-router-dom'
import { ChevronRight, ClipboardList, Loader2, Search } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Input } from '@/components/ui/input'
import { usePickListsList } from '@/hooks/usePickLists'
import { formatSheetDateTime, timeLeftLabel } from '@/lib/pickList'
import type { PickListStatus } from '@/types/pickList'
import { PageHeading } from '@/components/layout/PageHeading'

const STATUS_FILTERS: { value: PickListStatus | 'ALL'; label: string }[] = [
  { value: 'ALL', label: 'All' },
  { value: 'OPEN', label: 'Not started' },
  { value: 'IN_PROGRESS', label: 'In progress' },
  { value: 'COMPLETED', label: 'Completed' },
]

const STATUS_META: Record<PickListStatus, { label: string; cls: string }> = {
  DRAFT: { label: 'Draft', cls: 'border-slate-200 bg-slate-50 text-slate-600' },
  OPEN: { label: 'Not started', cls: 'border-slate-200 bg-slate-50 text-slate-600' },
  IN_PROGRESS: { label: 'In progress', cls: 'border-sky-200 bg-sky-50 text-sky-700' },
  COMPLETED: { label: 'Completed', cls: 'border-emerald-200 bg-emerald-50 text-emerald-700' },
  CANCELLED: { label: 'Cancelled', cls: 'border-red-200 bg-red-50 text-red-700' },
}

// Per-user column layout. Code is locked (it identifies the pick list) and so
// is the chevron that opens it. See src/components/table/index.ts.
const PICK_LISTS_TABLE_ID = 'pick-lists'

const PICK_LISTS_COLUMNS: TableColumnDef[] = [
  { id: 'code', header: 'Code', locked: true },
  { id: 'orderReference', header: 'Order reference' },
  { id: 'destination', header: 'Destination' },
  { id: 'progress', header: 'Progress' },
  { id: 'status', header: 'Status' },
  { id: 'assignedTo', header: 'Assigned to' },
  { id: 'requiredBy', header: 'Required by' },
  { id: 'open', header: 'Open', locked: true },
]

export default function PickListsPage() {
  const cols = useTableColumns(PICK_LISTS_TABLE_ID, PICK_LISTS_COLUMNS)
  const navigate = useNavigate()
  const [status, setStatus] = useState<PickListStatus | 'ALL'>('ALL')
  const [query, setQuery] = useState('')

  const { data, isLoading, error } = usePickListsList({
    status: status === 'ALL' ? undefined : status,
    q: query.trim() || undefined,
  })

  const rows = data?.items ?? []

  return (
    <div className='space-y-3 p-4 md:p-6'>
      <nav className='flex items-center gap-1 text-xs text-muted-foreground'>
        <Link to='/inventory' className='hover:text-foreground'>
          Inventory
        </Link>
        <ChevronRight className='h-3 w-3' />
        <span className='font-medium text-foreground'>Pick lists</span>
      </nav>

      <PageHeading title='Pick lists' description='Bills of material to collect, grouped by storage zone with FEFO lot suggestions.' />

      <div className='flex flex-wrap items-center gap-2 rounded-xl border bg-card p-2'>
        <div className='flex flex-wrap items-center gap-1'>
          {STATUS_FILTERS.map(f => (
            <button
              key={f.value}
              type='button'
              onClick={() => setStatus(f.value)}
              className={cn(
                'rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors',
                status === f.value
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground',
              )}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className='relative ml-auto w-full sm:w-72'>
          <Search className='pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground' />
          <Input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder='Search code, destination or order reference'
            className='h-8 pl-8 text-xs'
          />
        </div>
      </div>

      {error && (
        <p className='text-sm text-destructive'>
          {error instanceof Error ? error.message : 'Could not load pick lists.'}
        </p>
      )}

      {isLoading ? (
        <div className='flex items-center gap-2 p-8 text-sm text-muted-foreground'>
          <Loader2 className='h-4 w-4 animate-spin' />
          Loading pick lists…
        </div>
      ) : rows.length === 0 ? (
        <div className='flex flex-col items-center gap-2 rounded-xl border bg-card p-12 text-center'>
          <ClipboardList className='h-8 w-8 text-muted-foreground/40' />
          <p className='text-sm font-medium'>No pick lists yet</p>
          <p className='max-w-sm text-xs text-muted-foreground'>
            Generate one from a case on the Transplant page — its actions menu has “Review
            inventory”.
          </p>
        </div>
      ) : (
        <>
        <div className='overflow-x-persistent rounded-xl border bg-card'>
          <table className='w-full min-w-[900px] text-sm'>
            <thead>
              <tr className='border-b bg-muted/40 text-[10px] uppercase tracking-wider text-muted-foreground'>
                {cols.visible.map(({ def }) => (
                  <ResizableTh
                    key={def.id}
                    columnId={def.id}
                    columns={cols}
                    label={def.header}
                    className={
                      def.id === 'open' ? 'w-8 px-4 py-2.5' : 'px-4 py-2.5 text-left font-semibold'
                    }
                    resizable={def.id !== 'open'}
                  >
                    {def.id === 'open' ? null : def.header}
                  </ResizableTh>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => {
                const p = row.summary.progress
                const pct = p.total > 0 ? Math.round((p.picked / p.total) * 100) : 0
                const due = timeLeftLabel(row.requiredBy)
                const meta = STATUS_META[row.status]
                return (
                  <tr
                    key={row.id ?? row.code}
                    role='link'
                    tabIndex={0}
                    onClick={() => navigate(`/inventory/pick-lists/${row.id}`)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        navigate(`/inventory/pick-lists/${row.id}`)
                      }
                    }}
                    className={cn(
                      'cursor-pointer border-b transition-colors last:border-0 hover:bg-muted/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
                      i % 2 === 1 && 'bg-muted/10',
                    )}
                  >
                    <ColumnCells
                      columns={cols}
                      cells={{
                        code: (
                          <td className='px-4 py-3'>
                            <div className='font-semibold'>{row.code}</div>
                            <div className='text-xs text-muted-foreground'>rev {row.revision}</div>
                          </td>
                        ),
                        orderReference: <td className='px-4 py-3'>{row.orderReference ?? '—'}</td>,
                        destination: <td className='px-4 py-3'>{row.destination ?? '—'}</td>,
                        progress: (
                          <td className='px-4 py-3'>
                            <div className='flex items-center gap-2'>
                              <div className='h-1.5 w-20 overflow-hidden rounded-full bg-slate-200'>
                                <div
                                  className='h-full rounded-full bg-emerald-500'
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                              <span className='text-xs tabular-nums text-muted-foreground'>
                                {p.picked}/{p.total}
                              </span>
                            </div>
                          </td>
                        ),
                        status: (
                          <td className='px-4 py-3'>
                            <span
                              className={cn(
                                'inline-flex rounded-full border px-2 py-0.5 text-[11px] font-medium',
                                meta.cls,
                              )}
                            >
                              {meta.label}
                            </span>
                          </td>
                        ),
                        assignedTo: <td className='px-4 py-3 text-xs'>{row.assignedToName ?? 'Unassigned'}</td>,
                        requiredBy: (
                          <td className='px-4 py-3 text-xs'>
                            <span className={cn(due?.overdue && 'font-semibold text-destructive')}>
                              {formatSheetDateTime(row.requiredBy)}
                            </span>
                          </td>
                        ),
                        open: (
                          <td className='px-4 py-3'>
                            <ChevronRight className='h-4 w-4 text-muted-foreground/40' />
                          </td>
                        ),
                      }}
                    />
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        </>
      )}
    </div>
  )
}
